export const BANK_IMPORT_SOURCE_TYPE = 'credit_agricole_csv_v1';

export interface ParsedBankCsvRow {
  client_row_id: string;
  source_fingerprint: string;
  raw_bank_date: string;
  transaction_date: string;
  direction: 'debit';
  amount: number;
  currency_code: string;
  raw_label: string;
  normalized_label: string;
  merchant_key?: string;
}

export interface ParsedBankCsvResult {
  source_type: typeof BANK_IMPORT_SOURCE_TYPE;
  debitRows: ParsedBankCsvRow[];
  creditCount: number;
  skippedCount: number;
}

function decodeBankCsv(bytes: ArrayBuffer): string {
  try {
    return new TextDecoder('windows-1252').decode(bytes);
  } catch {
    return new TextDecoder('utf-8').decode(bytes);
  }
}

function parseDelimitedRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (char === '\r') continue;

    if (char === '"') {
      if (inQuotes && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ';' && !inQuotes) {
      row.push(field);
      field = '';
      continue;
    }

    if (char === '\n' && !inQuotes) {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      continue;
    }

    field += char;
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
}

function stripAccents(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function normalizeHeader(value: string): string {
  return stripAccents(value).toLowerCase().trim();
}

function parseFrenchAmount(value: string | undefined): number {
  if (!value?.trim()) return 0;
  // Thousands may be grouped with spaces/NBSP ("1 234,56") or dots
  // ("1.234,56"). When a comma is present it is the decimal separator and any
  // dots are grouping \u2014 previously "1.234,56" became "1.234.56" -> NaN -> 0
  // and the row was silently dropped, i.e. the largest transactions vanished.
  let normalized = value.replace(/\s|\u00a0/g, '');
  if (normalized.includes(',')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseFrenchDate(value: string): string | null {
  const match = value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  return `${match[3]}-${match[2]}-${match[1]}`;
}

function compactWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function parseCardTransactionDate(rawLabel: string, bankDate: string): string | null {
  const compact = compactWhitespace(rawLabel);
  if (!/PAIEMENT PAR CARTE/i.test(compact)) return null;

  const matches = [...compact.matchAll(/\b(\d{2})\/(\d{2})\b/g)];
  const last = matches[matches.length - 1];
  if (!last) return null;

  const bank = new Date(`${bankDate}T00:00:00.000Z`);
  const candidate = new Date(Date.UTC(bank.getUTCFullYear(), Number(last[2]) - 1, Number(last[1])));
  const allowedFutureMs = 7 * 24 * 60 * 60 * 1000;
  if (candidate.getTime() - bank.getTime() > allowedFutureMs) {
    candidate.setUTCFullYear(candidate.getUTCFullYear() - 1);
  }

  return candidate.toISOString().slice(0, 10);
}

export function normalizeBankLabel(rawLabel: string): string {
  return compactWhitespace(rawLabel)
    .replace(/paiement par carte/ig, ' ')
    .replace(/\bX\d{3,}\b/g, ' ')
    .replace(/\b\d{2}\/\d{2}\b\s*$/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function getMerchantKey(normalizedLabel: string): string | undefined {
  const normalized = stripAccents(normalizedLabel)
    .toUpperCase()
    .replace(/[^A-Z0-9* ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized || undefined;
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Fingerprint a row, disambiguating identical rows within the same file.
 *
 * Two genuinely distinct transactions can be byte-identical in the export
 * (same day, same merchant, same amount — e.g. two coffees). The bank CSV is
 * the source of truth, so each occurrence must import as its own expense:
 * the 2nd, 3rd, … identical row gets an occurrence ordinal mixed into the
 * hash. The FIRST occurrence keeps the exact legacy payload (no ordinal) so
 * fingerprints of rows imported before this fix still match on re-upload.
 */
async function fingerprintRow(
  input: {
    raw_bank_date: string;
    transaction_date: string;
    direction: 'debit';
    amount: number;
    currency_code: string;
    raw_label: string;
  },
  occurrenceCounts: Map<string, number>
) {
  const basePayload = JSON.stringify({
    source_type: BANK_IMPORT_SOURCE_TYPE,
    raw_bank_date: input.raw_bank_date,
    transaction_date: input.transaction_date,
    direction: input.direction,
    amount_cents: Math.round(input.amount * 100),
    currency_code: input.currency_code,
    raw_label: compactWhitespace(input.raw_label),
  });

  const occurrence = (occurrenceCounts.get(basePayload) ?? 0) + 1;
  occurrenceCounts.set(basePayload, occurrence);

  return sha256(occurrence === 1 ? basePayload : `${basePayload}#occurrence=${occurrence}`);
}

export async function parseBankCsv(bytes: ArrayBuffer): Promise<ParsedBankCsvResult> {
  const text = decodeBankCsv(bytes);
  const rows = parseDelimitedRows(text).filter((row) => row.some((field) => field.trim()));
  const headerIndex = rows.findIndex((row) =>
    normalizeHeader(row[0]) === 'date'
    && normalizeHeader(row[1]).startsWith('libell')
    && normalizeHeader(row[2]).includes('debit')
    && normalizeHeader(row[3]).includes('credit')
  );

  if (headerIndex < 0) {
    throw new Error('Could not find the transaction table in this CSV.');
  }

  const debitRows: ParsedBankCsvRow[] = [];
  let creditCount = 0;
  let skippedCount = 0;
  const occurrenceCounts = new Map<string, number>();

  const dataRows = rows.slice(headerIndex + 1);
  for (const [index, row] of dataRows.entries()) {
    const rawBankDate = parseFrenchDate(row[0] || '');
    if (!rawBankDate) {
      skippedCount += 1;
      continue;
    }

    const rawLabel = row[1] || '';
    const debit = parseFrenchAmount(row[2]);
    const credit = parseFrenchAmount(row[3]);

    if (credit > 0) {
      creditCount += 1;
      continue;
    }

    if (debit <= 0) {
      skippedCount += 1;
      continue;
    }

    const transactionDate = parseCardTransactionDate(rawLabel, rawBankDate) || rawBankDate;
    const normalizedLabel = normalizeBankLabel(rawLabel);
    const merchantKey = getMerchantKey(normalizedLabel);
    const sourceFingerprint = await fingerprintRow({
      raw_bank_date: rawBankDate,
      transaction_date: transactionDate,
      direction: 'debit',
      amount: debit,
      currency_code: 'EUR',
      raw_label: rawLabel,
    }, occurrenceCounts);

    debitRows.push({
      client_row_id: `row-${index}-${sourceFingerprint.slice(0, 8)}`,
      source_fingerprint: sourceFingerprint,
      raw_bank_date: rawBankDate,
      transaction_date: transactionDate,
      direction: 'debit',
      amount: debit,
      currency_code: 'EUR',
      raw_label: rawLabel,
      normalized_label: normalizedLabel || compactWhitespace(rawLabel),
      merchant_key: merchantKey,
    });
  }

  return {
    source_type: BANK_IMPORT_SOURCE_TYPE,
    debitRows,
    creditCount,
    skippedCount,
  };
}
